import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, Trash2, ExternalLink, Settings } from 'lucide-react';
import { api } from '../lib/api';
import { Dialog } from '../components/Dialog';
import { useToast } from '../components/Toast';

export default function TrunkTemplates() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ['trunk-templates'], queryFn: () => api.trunkTemplates.list() });
  const [applyOpen, setApplyOpen] = useState<any>(null);
  const [applyForm, setApplyForm] = useState<any>({});

  const apply = useMutation({
    mutationFn: () => api.trunkTemplates.apply(applyForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trunks'] }); setApplyOpen(null); toast('success', 'Trunk created'); },
    onError: (e: any) => toast('error', e.message),
  });
  const upload = useMutation({
    mutationFn: (file: File) => api.trunkTemplates.upload(file),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trunk-templates'] }); toast('success', 'Template uploaded'); },
    onError: (e: any) => toast('error', e.message),
  });
  const remove = useMutation({
    mutationFn: (filename: string) => api.trunkTemplates.remove(filename),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trunk-templates'] }),
  });

  function openApply(t: any) {
    setApplyForm({ template_filename: t.filename, source: t.source === 'built-in' ? 'builtin' : 'custom', name: t.name });
    setApplyOpen(t);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Trunk Provider Templates</h1>
        <label className="btn-primary inline-flex items-center gap-2 cursor-pointer">
          <Upload size={14} /> Upload custom XML
          <input type="file" accept=".xml,.pv.xml" className="hidden" onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])} />
        </label>
      </div>
      <p className="text-sm text-textSecondary">Built-in templates are authored from public carrier documentation. Custom templates uploaded by you live alongside.</p>

      <h2 className="text-lg font-semibold pt-2">Built-in</h2>
      <Grid templates={data?.builtin || []} onApply={openApply} />

      <h2 className="text-lg font-semibold pt-4">Custom uploads</h2>
      {(data?.custom || []).length === 0 ? (
        <div className="text-textSecondary text-sm">No custom templates uploaded.</div>
      ) : (
        <Grid templates={data?.custom || []} onApply={openApply} onRemove={(t: any) => remove.mutate(t.filename)} />
      )}

      <Dialog open={!!applyOpen} onClose={() => setApplyOpen(null)} title={`Use ${applyOpen?.name}`} size="md"
        footer={<><button onClick={() => setApplyOpen(null)} className="btn-ghost">Cancel</button><button onClick={() => apply.mutate()} className="btn-primary">Create trunk</button></>}>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">Trunk name *</label>
            <input className="input" value={applyForm.name || ''} onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Registrar username (if registration-based)</label>
            <input className="input font-mono" value={applyForm.registrar_user || ''} onChange={(e) => setApplyForm({ ...applyForm, registrar_user: e.target.value })} placeholder="phone number / SIP user" />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Registrar password</label>
            <input className="input font-mono" type="password" value={applyForm.registrar_pass || ''} onChange={(e) => setApplyForm({ ...applyForm, registrar_pass: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Primary DID (optional)</label>
            <input className="input font-mono" value={applyForm.did || ''} onChange={(e) => setApplyForm({ ...applyForm, did: e.target.value })} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function Grid({ templates, onApply, onRemove }: any) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {templates.map((t: any) => (
        <div key={t.filename} className="card flex flex-col">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{t.name}</h3>
            <span className="text-xs text-textSecondary">{t.templatetype || 'shared'}</span>
          </div>
          <p className="text-xs text-textSecondary mt-1">{t.description}</p>
          <div className="mt-2 text-xs text-textSecondary flex flex-wrap gap-1">
            {(t.tags || []).map((tag: string) => <span key={tag} className="bg-elevated px-1.5 py-0.5 rounded">{tag}</span>)}
          </div>
          <div className="mt-3 pt-3 border-t border-border flex gap-2 items-center">
            {t.url && <a href={t.url} target="_blank" rel="noreferrer" className="btn-ghost p-1" title="Carrier docs"><ExternalLink size={12} /></a>}
            <button onClick={() => onApply(t)} className="btn-primary text-xs flex-1 inline-flex items-center gap-1 justify-center"><Settings size={12} /> Use</button>
            {onRemove && <button onClick={() => onRemove(t)} className="btn-ghost p-1 text-danger"><Trash2 size={12} /></button>}
          </div>
        </div>
      ))}
    </div>
  );
}
